import { useEffect, useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Plus, Play, Square, Pause, MessageSquare, Send, AlertTriangle, Search, Keyboard, Command, Trash2, ArrowRightLeft, Check, X, RotateCcw, OctagonX, ArrowUpNarrowWide, ArrowDownWideNarrow } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTaskTimer } from '../hooks/useTaskTimer';
import { CreateTaskDialog } from '../components/CreateTaskDialog';
import { STATUS_COLORS, STATUS_LABELS } from '../utils/constants';
import { AutoPendingBadge } from '../components/AutoPendingBadge';

const STATUS_OPTIONS = [
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-500' },
  { value: 'pending_with_client', label: 'Pending with Client', color: 'bg-orange-500' },
  { value: 'pending_with_member', label: 'Pending with Member', color: 'bg-purple-500' },
  { value: 'on_hold_client', label: 'On Hold', color: 'bg-amber-500' },
  { value: 'completed', label: 'Completed', color: 'bg-green-500' },
];

export default function Tasks() {
  const navigate = useNavigate();
  const location = useLocation();
  const queueFilter = new URLSearchParams(location.search).get('queue'); // 'mine' enables My-Queue mode
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // 'all' | 'mine' | userId
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem('tasks.sortBy') || 'priority'; } catch { return 'priority'; }
  });
  const [sortDir, setSortDir] = useState(() => {
    try { return localStorage.getItem('tasks.sortDir') || 'asc'; } catch { return 'asc'; }
  });

  // --- Pagination state -----------------------------------------------------
  // Server-side paginates against `/api/tasks?page=&page_size=` so we don't
  // ship 20k rows to the browser. Page size of 100 keeps each request small
  // while still giving the user something to scroll.
  const PAGE_SIZE = 100;
  const [page, setPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const { activeTimers, timerDisplays, startTimer, pauseTimer, stopTimer, loadTimers } = useTaskTimer();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [commentTaskId, setCommentTaskId] = useState(null);
  const [quickComment, setQuickComment] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [statusOpenIndex, setStatusOpenIndex] = useState(-1);
  const searchRef = useRef(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [lastClickedIdx, setLastClickedIdx] = useState(null);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);
  const [pendingMemberTaskId, setPendingMemberTaskId] = useState(null);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [deleteTaskInfo, setDeleteTaskInfo] = useState(null); // {is_recurring, parent_task_id, title}
  const [deleteSeries, setDeleteSeries] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [pendingTransfers, setPendingTransfers] = useState({ incoming: [], outgoing: [] });
  const [transferTask, setTransferTask] = useState(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferToId, setTransferToId] = useState('');
  const [transferComment, setTransferComment] = useState('');

  useEffect(() => { fetchData(); fetchTransfers(); }, [page, filterStatus, assigneeFilter, sortBy, sortDir, queueFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  // When toggling between Tasks ↔ My Queue, force back to page 1 so the
  // user doesn't land on an out-of-range page (e.g. page 5 of an empty queue).
  useEffect(() => { setPage(1); }, [queueFilter]);
  // Debounced search → reset to page 1 only when the search term actually
  // CHANGES. We compare against the previously-applied value (via ref) so
  // the initial mount — when searchTerm is just its initial empty value —
  // doesn't trigger a redundant fetch on top of the [page,...] effect above.
  const lastSearchRef = useRef(searchTerm);
  useEffect(() => {
    if (lastSearchRef.current === searchTerm) return;
    lastSearchRef.current = searchTerm;
    const t = setTimeout(() => {
      if (page === 1) fetchData();
      else setPage(1); // setting page triggers fetch via the [page,...] effect
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh on WebSocket notifications. Polling was removed in favour of
  // event-driven updates — these names MUST match what the backend dispatches
  // (see broadcast_to_user calls in routers/task_routes.py).
  useEffect(() => {
    const handler = (e) => {
      const type = e?.detail?.type;
      if ([
        'task_assigned',
        'status_update',
        'review_request',
        'transfer_accepted',
        'transfer_rejected',
      ].includes(type)) {
        fetchData();
      }
      if (type === 'task_transfer_request') {
        fetchTransfers();
        fetchData(); // an incoming transfer also affects the visible task row
      }
    };
    window.addEventListener('ws-notification', handler);
    return () => window.removeEventListener('ws-notification', handler);
  }, [page, filterStatus, assigneeFilter, sortBy, sortDir, searchTerm, queueFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (assigneeFilter === 'mine' && currentUser?.id) params.set('assignee_id', currentUser.id);
      else if (assigneeFilter !== 'all' && assigneeFilter !== 'mine') params.set('assignee_id', assigneeFilter);
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      if (queueFilter === 'mine') {
        params.set('status', 'pending_with_member');
        if (currentUser?.id) params.set('assignee_id', ''); // ignore assignee in queue mode
        params.delete('assignee_id');
      }

      const [tasksRes, clientsRes, usersRes, servicesRes, userRes] = await Promise.all([
        api.get(`/tasks?${params.toString()}`),
        api.get('/clients'),
        api.get('/users'),
        api.get('/services'),
        api.get('/users/me'),
      ]);
      const payload = tasksRes.data || {};
      // Server returns { items, total, page, page_size, has_more } when page param is sent
      let items = Array.isArray(payload) ? payload : (payload.items || []);
      // My-Queue extra filter (server returns all pending_with_member; narrow to me)
      if (queueFilter === 'mine' && userRes.data?.id) {
        items = items.filter(t => t.pending_with_member_id === userRes.data.id);
      }
      setTasks(items);
      setTotalTasks(Array.isArray(payload) ? items.length : (payload.total ?? items.length));
      setHasMore(Array.isArray(payload) ? false : !!payload.has_more);
      setClients(clientsRes.data); setUsers(usersRes.data);
      setServices(servicesRes.data); setCurrentUser(userRes.data);
      await loadTimers();
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.company || '-';
  const getUserName = (id) => users.find(u => u.id === id)?.full_name || '-';
  const now = new Date();

  const fetchTransfers = async () => {
    try {
      const res = await api.get('/task-transfers/pending');
      setPendingTransfers(res.data);
    } catch (err) { console.warn('Pending transfers fetch failed:', err); }
  };

  const handleAcceptTransfer = async (transferId) => {
    try {
      const res = await api.put(`/task-transfers/${transferId}/accept`);
      toast.success(res.data.message);
      fetchData(); fetchTransfers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to accept transfer'); }
  };

  const handleRejectTransfer = async (transferId) => {
    try {
      const res = await api.put(`/task-transfers/${transferId}/reject`);
      toast.success(res.data.message);
      fetchTransfers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reject transfer'); }
  };

  const handleSubmitTransfer = async () => {
    if (!transferTask || !transferToId) return;
    try {
      await api.post(`/tasks/${transferTask.id}/transfer-request`, {
        to_user_id: transferToId, comment: transferComment || null
      });
      const targetUser = users.find(u => u.id === transferToId);
      toast.success(`Transfer request sent to ${targetUser?.full_name || 'user'}. Awaiting acceptance.`);
      setTransferDialogOpen(false); setTransferTask(null); setTransferToId(''); setTransferComment('');
      fetchData(); fetchTransfers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create transfer request'); }
  };

  const getPriority = (task) => {
    if (task.status === 'completed') return 3;
    const deadline = new Date(task.deadline);
    if (deadline < now) return 0;
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    if (deadline < tomorrow) return 1;
    return 2;
  };

  const getPriorityIndicator = (task) => {
    const p = getPriority(task);
    if (p === 0) return 'border-l-4 border-l-red-500 bg-red-50/40';
    if (p === 1) return 'border-l-4 border-l-orange-400 bg-orange-50/30';
    return 'border-l-4 border-l-transparent';
  };

  const handleQuickStatus = async (taskId, newStatus, pendingMemberId) => {
    if (newStatus === 'pending_with_member' && !pendingMemberId) {
      setPendingMemberTaskId(taskId); setStatusOpenIndex(-1); return;
    }
    if (newStatus === 'completed') {
      const task = tasks.find(t => t.id === taskId);
      if ((task?.time_logs || []).reduce((s, l) => s + (l.hours || 0), 0) <= 0) {
        toast.warning('No time logged. Please log time before marking as completed.', { duration: 5000 });
        setStatusOpenIndex(-1); return;
      }
    }
    try {
      const payload = { status: newStatus };
      if (newStatus === 'pending_with_member' && pendingMemberId) payload.pending_with_member_id = pendingMemberId;
      await api.put(`/tasks/${taskId}/status`, payload);
      toast.success(`Status updated to ${STATUS_LABELS[newStatus]}`);
      setPendingMemberTaskId(null); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update status'); }
  };

  const canStopRecurrence = (task) => {
    if (!task?.is_recurring) return false;
    const role = currentUser?.role;
    if (role === 'super_admin' || role === 'admin') return true;
    // Members: only on tasks they own or created
    return task.assignee_id === currentUser?.id || task.created_by === currentUser?.id;
  };

  const handleHeaderSort = (col) => {
    if (sortBy === col) {
      const v = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(v);
      try { localStorage.setItem('tasks.sortDir', v); } catch { /* noop */ }
    } else {
      setSortBy(col);
      setSortDir('asc');
      try {
        localStorage.setItem('tasks.sortBy', col);
        localStorage.setItem('tasks.sortDir', 'asc');
      } catch { /* noop */ }
    }
    setPage(1);
  };

  const handleStopRecurrence = async (task) => {
    if (!task?.id) return;
    const ok = window.confirm(
      `Stop recurrence on "${task.title}"?\n\n` +
      `The current task stays open. No NEW instances will be created automatically when this one is completed. You can re-enable it later from the task detail page.`
    );
    if (!ok) return;
    try {
      const res = await api.put(`/tasks/${task.id}/stop-recurrence`);
      toast.success(`Recurrence stopped (${res.data.tasks_updated} task${res.data.tasks_updated === 1 ? '' : 's'} updated)`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to stop recurrence');
    }
  };



  const handleQuickComment = async (taskId) => {
    if (!quickComment.trim()) return;
    try {
      await api.post(`/tasks/${taskId}/comments`, { text: quickComment.trim() });
      toast.success('Comment added'); setQuickComment(''); setCommentTaskId(null);
    } catch { toast.error('Failed to add comment'); }
  };

  const canCreate = currentUser?.role === 'super_admin' || currentUser?.role === 'admin' || currentUser?.role === 'member';

  const toggleTaskSelection = (taskId, idx, shiftKey) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (shiftKey && lastClickedIdx !== null) {
        const start = Math.min(lastClickedIdx, idx), end = Math.max(lastClickedIdx, idx);
        for (let i = start; i <= end; i++) next.add(filtered[i].id);
      } else { if (next.has(taskId)) next.delete(taskId); else next.add(taskId); }
      return next;
    });
    setLastClickedIdx(idx);
  };

  const toggleSelectAll = () => {
    setSelectedTaskIds(prev => prev.size === filtered.length ? new Set() : new Set(filtered.map(t => t.id)));
  };

  const handleBulkStatus = async (status) => {
    if (status === 'completed') {
      const noTime = [...selectedTaskIds].map(id => tasks.find(t => t.id === id)).filter(t => t && (t.time_logs || []).reduce((s, l) => s + (l.hours || 0), 0) <= 0);
      if (noTime.length > 0) { toast.warning(`Cannot complete: ${noTime.slice(0, 3).map(t => t.title).join(', ')} have no time logged.`, { duration: 5000 }); return; }
    }
    try {
      const res = await api.put('/tasks/bulk/status', { task_ids: [...selectedTaskIds], status });
      toast.success(res.data.message); setSelectedTaskIds(new Set()); setBulkStatusOpen(false); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Bulk status update failed'); }
  };

  const handleBulkReassign = async (assigneeId) => {
    try {
      const res = await api.put('/tasks/bulk/reassign', { task_ids: [...selectedTaskIds], new_assignee_id: assigneeId });
      toast.success(res.data.message); setSelectedTaskIds(new Set()); setBulkReassignOpen(false); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Bulk reassign failed'); }
  };

  const openDeleteDialog = (task) => {
    setDeleteTaskId(task.id);
    setDeleteTaskInfo({
      is_recurring: !!task.is_recurring || !!task.parent_task_id,
      parent_task_id: task.parent_task_id || null,
      title: task.title || '',
    });
    setDeleteSeries(false);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteTask = async () => {
    if (!deleteTaskId) return;
    try {
      const url = deleteSeries
        ? `/tasks/${deleteTaskId}?delete_series=true`
        : `/tasks/${deleteTaskId}`;
      const res = await api.delete(url);
      toast.success(res.data?.message || 'Task deleted');
      setIsDeleteDialogOpen(false);
      setDeleteTaskId(null);
      setDeleteTaskInfo(null);
      setDeleteSeries(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete task'); }
  };

  // Server already applied status/assignee/search/sort + admin RBAC,
  // so `filtered` is just the current page. We keep the variable name to
  // minimise diffs across the rest of the file.
  const filtered = tasks;

  // Keyboard shortcuts
  useEffect(() => {
    const isInputFocused = () => { const el = document.activeElement; return el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.contentEditable === 'true' || el.closest('[role="dialog"]')); };
    const handleKeyDown = (e) => {
      if (isInputFocused() || isCreateOpen) return;
      const key = e.key.toLowerCase();
      switch (key) {
        case 'arrowdown': e.preventDefault(); setSelectedIndex(prev => Math.min(prev + 1, filtered.length - 1)); break;
        case 'arrowup': e.preventDefault(); setSelectedIndex(prev => Math.max(prev - 1, 0)); break;
        case 'enter': e.preventDefault(); if (selectedIndex >= 0 && filtered[selectedIndex]) navigate(`/tasks/${filtered[selectedIndex].id}`); break;
        case 't': e.preventDefault(); if (selectedIndex >= 0 && filtered[selectedIndex]?.status !== 'completed') { const t = activeTimers[filtered[selectedIndex].id]; if (t?.status === 'running') pauseTimer(filtered[selectedIndex].id); else startTimer(filtered[selectedIndex].id); } break;
        case 's': e.preventDefault(); if (selectedIndex >= 0) setStatusOpenIndex(prev => prev === selectedIndex ? -1 : selectedIndex); break;
        case 'c': e.preventDefault(); if (selectedIndex >= 0 && filtered[selectedIndex]) setCommentTaskId(filtered[selectedIndex].id); break;
        case 'n': e.preventDefault(); if (canCreate) setIsCreateOpen(true); break;
        case '/': e.preventDefault(); searchRef.current?.focus(); break;
        case 'escape': setSelectedIndex(-1); setStatusOpenIndex(-1); setCommentTaskId(null); setSelectedTaskIds(new Set()); break;
        case '?': e.preventDefault(); setShowShortcuts(prev => !prev); break;
        case 'a': e.preventDefault(); toggleSelectAll(); break;
        default: break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, filtered, activeTimers, isCreateOpen, canCreate, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Only auto-scroll when the user uses keyboard nav (selectedIndex changes).
    // The dep on `filtered` was causing the list to jump back to the selected
    // row every time the 10-second background refresh re-built the filtered
    // array, which felt like the scroll "jumped to top" while the user was
    // mid-scroll.
    if (selectedIndex < 0) return;
    const el = document.querySelector(`[data-testid="task-row-${filtered[selectedIndex]?.id}"]`);
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8 min-h-full bg-slate-50">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="tasks-heading">
            {queueFilter === 'mine' ? 'My Queue' : (currentUser?.role === 'member' ? 'My Tasks' : 'Tasks')}
          </h1>
          <p className="text-slate-500 text-sm mt-1" data-testid="tasks-count">
            {queueFilter === 'mine'
              ? `${totalTasks} task${totalTasks === 1 ? '' : 's'} currently pending with you`
              : `${totalTasks.toLocaleString()} task${totalTasks === 1 ? '' : 's'}${totalTasks > PAGE_SIZE ? ` · showing ${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, totalTasks)}` : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {queueFilter === 'mine' && (
            <Button variant="outline" size="sm" onClick={() => navigate('/tasks')} data-testid="clear-queue-filter" className="text-xs">
              <X className="h-3.5 w-3.5 mr-1" /> Clear queue filter
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => setIsCreateOpen(true)} className="bg-[#5B6FD6] hover:bg-[#4a5bc5] shadow-md" data-testid="create-task-button">
              <Plus className="h-5 w-5 mr-2" /> Create Task
            </Button>
          )}
        </div>
      </div>

      {queueFilter === 'mine' && (
        <div className="mb-4 px-4 py-3 bg-purple-50 border border-purple-200 rounded-xl flex items-center gap-3" data-testid="my-queue-banner">
          <div className="h-9 w-9 rounded-lg bg-purple-100 flex items-center justify-center">
            <Send className="h-4 w-4 text-purple-700" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-purple-900">Your inbox</p>
            <p className="text-xs text-purple-700/80">These tasks were marked "Pending with Member" and routed to you. Pick one and move it forward.</p>
          </div>
        </div>
      )}

      {/* Search & Filter */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input ref={searchRef} placeholder="Search tasks, clients, members..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 bg-white" data-testid="task-search" />
        </div>
        <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
          <SelectTrigger className="w-44 bg-white" data-testid="task-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>

        {/* Assignee filter — defaults to everyone; "Mine" filters to current user */}
        <Select value={assigneeFilter} onValueChange={(v) => { setAssigneeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-48 bg-white" data-testid="task-filter-assignee"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Assignees</SelectItem>
            <SelectItem value="mine">Assigned to me</SelectItem>
            {users.filter(u => u.is_active && u.id !== currentUser?.id).map(u => (
              <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sort dropdown — persists choice per browser */}
        <Select
          value={sortBy}
          onValueChange={(v) => { setSortBy(v); setPage(1); try { localStorage.setItem('tasks.sortBy', v); } catch { /* noop */ } }}
        >
          <SelectTrigger className="w-44 bg-white" data-testid="task-sort-by"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="priority">Sort: Priority (smart)</SelectItem>
            <SelectItem value="deadline">Sort: Deadline</SelectItem>
            <SelectItem value="created">Sort: Created</SelectItem>
            <SelectItem value="title">Sort: Title</SelectItem>
            <SelectItem value="client">Sort: Client</SelectItem>
            <SelectItem value="assignee">Sort: Assignee</SelectItem>
            <SelectItem value="status">Sort: Status</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          onClick={() => { const v = sortDir === 'asc' ? 'desc' : 'asc'; setSortDir(v); setPage(1); try { localStorage.setItem('tasks.sortDir', v); } catch { /* noop */ } }}
          className="bg-white"
          title={`Toggle direction (currently ${sortDir === 'asc' ? 'ascending' : 'descending'})`}
          data-testid="task-sort-dir"
        >
          {sortDir === 'asc' ? <ArrowUpNarrowWide className="h-4 w-4" /> : <ArrowDownWideNarrow className="h-4 w-4" />}
        </Button>
      </div>

      {/* Pending Incoming Transfer Requests */}
      {pendingTransfers.incoming.length > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl overflow-hidden" data-testid="incoming-transfers-banner">
          <div className="px-4 py-2 bg-amber-100/60 border-b border-amber-200 flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-amber-600" />
            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Incoming Transfer Requests ({pendingTransfers.incoming.length})</span>
          </div>
          {pendingTransfers.incoming.map(tr => (
            <div key={tr.id} className="flex items-center justify-between px-4 py-3 border-b border-amber-100 last:border-0" data-testid={`incoming-transfer-${tr.id}`}>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-slate-900">{tr.task_title}</span>
                <span className="text-slate-400 mx-2">from</span>
                <span className="text-sm font-semibold text-[#5B6FD6]">{tr.from_user_name}</span>
                {tr.comment && <p className="text-xs text-slate-400 mt-0.5">{tr.comment}</p>}
              </div>
              <div className="flex items-center gap-2 ml-3">
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 h-8 px-3 text-xs" onClick={() => handleAcceptTransfer(tr.id)} data-testid={`accept-transfer-${tr.id}`}>
                  <Check className="h-3 w-3 mr-1" /> Accept
                </Button>
                <Button size="sm" variant="outline" className="h-8 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleRejectTransfer(tr.id)} data-testid={`reject-transfer-${tr.id}`}>
                  <X className="h-3 w-3 mr-1" /> Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pending Outgoing Transfer Requests */}
      {pendingTransfers.outgoing.length > 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl overflow-hidden" data-testid="outgoing-transfers-banner">
          <div className="px-4 py-2 bg-blue-100/60 border-b border-blue-200 flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Outgoing Transfers - Awaiting Acceptance ({pendingTransfers.outgoing.length})</span>
          </div>
          {pendingTransfers.outgoing.map(tr => (
            <div key={tr.id} className="flex items-center px-4 py-3 border-b border-blue-100 last:border-0" data-testid={`outgoing-transfer-${tr.id}`}>
              <span className="text-sm font-medium text-slate-900">{tr.task_title}</span>
              <span className="text-slate-400 mx-2">to</span>
              <span className="text-sm font-semibold text-[#5B6FD6]">{tr.to_user_name}</span>
              <Badge variant="outline" className="ml-3 text-[10px] bg-amber-50 text-amber-600 border-amber-200">Pending</Badge>
            </div>
          ))}
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedTaskIds.size > 0 && canCreate && (
        <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-[#5B6FD6]/5 border border-[#5B6FD6]/20 rounded-xl" data-testid="bulk-action-bar">
          <span className="text-sm font-semibold text-[#5B6FD6]">{selectedTaskIds.size} selected</span>
          <div className="h-4 w-px bg-[#5B6FD6]/20" />
          <Popover open={bulkStatusOpen} onOpenChange={setBulkStatusOpen}>
            <PopoverTrigger asChild><Button size="sm" variant="outline" className="text-xs h-8" data-testid="bulk-status-btn">Change Status</Button></PopoverTrigger>
            <PopoverContent className="w-48 p-1" align="start">
              {STATUS_OPTIONS.map(s => (
                <button key={s.value} onClick={() => handleBulkStatus(s.value)} className="w-full text-left text-sm px-3 py-2 rounded hover:bg-slate-100 flex items-center gap-2" data-testid={`bulk-status-option-${s.value}`}>
                  <div className={`w-2 h-2 rounded-full ${s.color}`} /> {s.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <Popover open={bulkReassignOpen} onOpenChange={setBulkReassignOpen}>
            <PopoverTrigger asChild><Button size="sm" variant="outline" className="text-xs h-8" data-testid="bulk-reassign-btn">Reassign</Button></PopoverTrigger>
            <PopoverContent className="w-56 p-1" align="start">
              {users.map(u => (
                <button key={u.id} onClick={() => handleBulkReassign(u.id)} className="w-full text-left text-sm px-3 py-2 rounded hover:bg-slate-100 flex items-center gap-2" data-testid={`bulk-reassign-option-${u.id}`}>
                  <div className="h-5 w-5 rounded-full bg-gradient-to-br from-[#5B6FD6] to-[#10B981] flex items-center justify-center text-white text-[10px] font-bold">{u.full_name?.charAt(0)}</div>
                  <span>{u.full_name}</span><span className="text-xs text-slate-400 capitalize ml-auto">{u.role}</span>
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <button onClick={() => setSelectedTaskIds(new Set())} className="text-xs text-slate-500 hover:text-slate-700 ml-auto" data-testid="bulk-deselect-btn">Deselect All</button>
        </div>
      )}

      {/* Task List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="task-list">
        <div className="overflow-x-auto">
        <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wider items-center min-w-[700px]">
          {canCreate && <div className="col-span-1 flex items-center"><Checkbox checked={filtered.length > 0 && selectedTaskIds.size === filtered.length} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" className="border-slate-400 data-[state=checked]:bg-[#5B6FD6]" /></div>}
          <SortHeader col="title" label="Task / Client" sortBy={sortBy} sortDir={sortDir} onSort={handleHeaderSort} className={canCreate ? 'col-span-3' : 'col-span-3'} />
          <SortHeader col="assignee" label="Assigned" sortBy={sortBy} sortDir={sortDir} onSort={handleHeaderSort} className="col-span-2" />
          <SortHeader col="deadline" label="Due Date" sortBy={sortBy} sortDir={sortDir} onSort={handleHeaderSort} className="col-span-1" />
          <SortHeader col="created" label="Ageing" sortBy={sortBy} sortDir={sortDir} onSort={handleHeaderSort} className="col-span-1" />
          <SortHeader col="status" label="Status" sortBy={sortBy} sortDir={sortDir} onSort={handleHeaderSort} className="col-span-2" />
          <div className={canCreate ? 'col-span-2 text-right' : 'col-span-3 text-right'}>Actions</div>
        </div>

        {filtered.length === 0 ? <div className="py-12 text-center text-slate-400" data-testid="task-list-empty">No tasks found</div> : (
          <div className="divide-y divide-slate-100">
            {filtered.map((task, idx) => {
              const pendingWithMe = task.status === 'pending_with_member' && task.pending_with_member_id === currentUser?.id;
              return (
              <div key={task.id} onClick={() => setSelectedIndex(idx)}
                className={`grid grid-cols-12 gap-2 items-center px-5 py-3 transition-colors cursor-pointer min-w-[700px] ${getPriorityIndicator(task)} ${selectedTaskIds.has(task.id) ? 'bg-[#5B6FD6]/8' : pendingWithMe ? 'bg-purple-50 border-l-4 border-l-purple-400' : ''} ${selectedIndex === idx ? 'bg-[#5B6FD6]/10 ring-2 ring-inset ring-[#5B6FD6]/40' : 'hover:bg-slate-50'}`} data-testid={`task-row-${task.id}`}>
                {canCreate && (
                  <div className="col-span-1 flex items-center" onClick={e => e.stopPropagation()}>
                    <Checkbox checked={selectedTaskIds.has(task.id)} onCheckedChange={() => toggleTaskSelection(task.id, idx, false)}
                      onClick={e => { if (e.shiftKey) { e.preventDefault(); toggleTaskSelection(task.id, idx, true); }}}
                      data-testid={`task-checkbox-${task.id}`} className="border-slate-300 data-[state=checked]:bg-[#5B6FD6]" />
                  </div>
                )}
                <div className={canCreate ? 'col-span-3 min-w-0' : 'col-span-3 min-w-0'}>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Link to={`/tasks/${task.id}`} className="font-medium text-sm text-slate-900 hover:text-[#5B6FD6] truncate" data-testid={`task-link-${task.id}`}>{task.title}</Link>
                    {task.is_recurring && (
                      <RecurringBadge task={task} canStop={canStopRecurrence(task)} onStop={() => handleStopRecurrence(task)} />
                    )}
                    {task.estimate_hours > 0 && (() => {
                      const actual = (task.time_logs || []).reduce((s, l) => s + (l.hours || 0), 0);
                      const pct = (actual - task.estimate_hours) / task.estimate_hours * 100;
                      const color =
                        pct <= 0 ? 'bg-emerald-100 text-emerald-700 ring-emerald-200'
                        : pct <= 25 ? 'bg-amber-100 text-amber-700 ring-amber-200'
                        : 'bg-red-100 text-red-700 ring-red-200';
                      return (
                        <span
                          className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full ring-1 flex-shrink-0 ${color}`}
                          title={`Estimate ${task.estimate_hours}h · Actual ${actual.toFixed(2)}h`}
                          data-testid={`estimate-pill-${task.id}`}
                        >
                          {task.estimate_hours}h
                        </span>
                      );
                    })()}
                  </div>
                  <span className="text-xs text-slate-500">{getClientName(task.client_id)}</span>
                </div>
                <div className="col-span-2 text-sm text-slate-600 truncate">{getUserName(task.assignee_id)}</div>
                <div className="col-span-1">
                  <span className={`text-sm ${getPriority(task) === 0 ? 'text-red-600 font-semibold' : getPriority(task) === 1 ? 'text-orange-600 font-medium' : 'text-slate-600'}`}>
                    {new Date(task.deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
                <div className="col-span-1" data-testid={`ageing-${task.id}`}>
                  {(() => {
                    const ref = task.updated_at || task.created_at;
                    if (!ref) return null;
                    const days = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
                    return (
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${days > 15 ? 'bg-red-100 text-red-700' : days > 7 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                        {days}d
                      </span>
                    );
                  })()}
                </div>
                <div className="col-span-2 flex items-center gap-1">
                  {task.status !== 'completed' && (task.time_logs || []).reduce((s, l) => s + (l.hours || 0), 0) <= 0 && !task.auto_pending_flipped_at && (
                    <span className="flex-shrink-0" title="No time logged" data-testid={`no-time-warn-${task.id}`}><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /></span>
                  )}
                  <AutoPendingBadge task={task} />
                  <Popover open={statusOpenIndex === idx} onOpenChange={open => { if (!open) setStatusOpenIndex(-1); else setStatusOpenIndex(idx); }}>
                    <PopoverTrigger asChild>
                      <button className={`inline-flex items-center gap-1 text-xs text-white px-2.5 py-1.5 rounded-full hover:opacity-90 ${STATUS_COLORS[task.status] || 'bg-slate-500'}`} data-testid={`status-btn-${task.id}`}>
                        <span>{STATUS_LABELS[task.status] || task.status}</span>
                        {task.status === 'pending_with_member' && task.pending_with_member_id && (() => {
                          const u = users.find(x => x.id === task.pending_with_member_id);
                          const name = u?.full_name?.split(' ')[0] || 'Unknown';
                          return <span className="bg-white/25 px-1.5 py-0.5 rounded-full text-[10px] font-semibold" data-testid={`pending-member-name-${task.id}`}>{name}</span>;
                        })()}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-1" align="start">
                      {STATUS_OPTIONS.map(s => (
                        <button key={s.value} onClick={() => { handleQuickStatus(task.id, s.value); setStatusOpenIndex(-1); }}
                          className={`w-full text-left text-sm px-3 py-2 rounded hover:bg-slate-100 flex items-center gap-2 ${task.status === s.value ? 'font-semibold bg-slate-50' : ''}`}
                          data-testid={`status-option-${task.id}-${s.value}`}><div className={`w-2 h-2 rounded-full ${s.color}`} /> {s.label}</button>
                      ))}
                    </PopoverContent>
                  </Popover>
                  <Popover open={pendingMemberTaskId === task.id} onOpenChange={open => { if (!open) setPendingMemberTaskId(null); }}>
                    <PopoverTrigger asChild><span /></PopoverTrigger>
                    <PopoverContent className="w-56 p-2" align="start">
                      <p className="text-xs font-semibold text-slate-500 mb-2 px-1">Select Member</p>
                      {users.filter(u => {
                        if (!u.id || u.is_active === false) return false;
                        if (u.id === currentUser?.id) return false;
                        if (u.role === 'super_admin') return true;
                        // Same team as logged-in user (parent admin OR direct reports)
                        const myAdminId = currentUser?.parent_admin_id || (currentUser?.role === 'admin' ? currentUser?.id : null);
                        if (currentUser?.role === 'super_admin') return true; // super admin can pick anyone
                        if (currentUser?.role === 'admin') {
                          return u.parent_admin_id === currentUser.id;
                        }
                        // member
                        if (myAdminId && u.id === myAdminId) return true;
                        if (myAdminId && u.parent_admin_id === myAdminId && u.role === 'member') return true;
                        return false;
                      }).map(u => (
                        <button key={u.id} onClick={() => handleQuickStatus(task.id, 'pending_with_member', u.id)}
                          className="w-full text-left text-sm px-3 py-2 rounded hover:bg-purple-50 flex items-center gap-2" data-testid={`pending-member-${task.id}-${u.id}`}>
                          <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold">{u.full_name?.charAt(0)}</div>
                          {u.full_name}
                          {u.role === 'super_admin' && <span className="text-[10px] text-red-500">(SA)</span>}
                          {u.role === 'admin' && <span className="text-[10px] text-blue-500">(A)</span>}
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                </div>
                <div className={`${canCreate ? 'col-span-2' : 'col-span-3'} flex items-center justify-end gap-1`}>
                  {task.status !== 'completed' && (() => {
                    const timer = activeTimers[task.id];
                    if (timer?.status === 'running') return (
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-mono text-red-600 bg-red-50 px-1.5 py-0.5 rounded">{timerDisplays[task.id] || '00:00:00'}</span>
                        <button onClick={e => { e.stopPropagation(); pauseTimer(task.id); }} className="p-1 bg-amber-500 text-white rounded hover:bg-amber-600" data-testid={`pause-timer-${task.id}`}><Pause className="h-3 w-3" /></button>
                        <button onClick={e => { e.stopPropagation(); stopTimer(task.id); fetchData(); }} className="p-1 bg-red-500 text-white rounded hover:bg-red-600" data-testid={`stop-timer-${task.id}`}><Square className="h-3 w-3" /></button>
                      </div>
                    );
                    if (timer?.status === 'paused') return (
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-mono text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded animate-pulse">{timerDisplays[task.id] || '00:00:00'}</span>
                        <button onClick={e => { e.stopPropagation(); startTimer(task.id); }} className="p-1 bg-[#10B981] text-white rounded hover:bg-[#0d9668]" data-testid={`resume-timer-${task.id}`}><Play className="h-3 w-3" /></button>
                        <button onClick={e => { e.stopPropagation(); stopTimer(task.id); fetchData(); }} className="p-1 bg-red-500 text-white rounded hover:bg-red-600" data-testid={`stop-timer-${task.id}`}><Square className="h-3 w-3" /></button>
                      </div>
                    );
                    return <button onClick={e => { e.stopPropagation(); startTimer(task.id); }} className="flex items-center gap-1 text-xs bg-[#10B981] text-white px-2.5 py-1.5 rounded-lg hover:bg-[#0d9668]" data-testid={`start-timer-${task.id}`}><Play className="h-3 w-3" /> Start</button>;
                  })()}
                  <Popover open={commentTaskId === task.id} onOpenChange={open => { if (!open) setCommentTaskId(null); }}>
                    <PopoverTrigger asChild>
                      <button onClick={e => { e.stopPropagation(); setCommentTaskId(task.id); }} className="p-1.5 text-slate-400 hover:text-[#5B6FD6] hover:bg-[#5B6FD6]/10 rounded-lg" data-testid={`comment-btn-${task.id}`}><MessageSquare className="h-4 w-4" /></button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-3" align="end">
                      <div className="flex gap-2">
                        <Input placeholder="Quick note..." value={quickComment} onChange={e => setQuickComment(e.target.value)} className="text-sm" data-testid={`quick-comment-input-${task.id}`}
                          onKeyDown={e => { if (e.key === 'Enter') handleQuickComment(task.id); }} />
                        <Button size="sm" onClick={() => handleQuickComment(task.id)} className="bg-[#5B6FD6] px-2" data-testid={`quick-comment-send-${task.id}`}><Send className="h-4 w-4" /></Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <button onClick={e => { e.stopPropagation(); setTransferTask(task); setTransferDialogOpen(true); }}
                    className="p-1.5 text-slate-400 hover:text-[#5B6FD6] hover:bg-[#5B6FD6]/10 rounded-lg" data-testid={`transfer-task-btn-${task.id}`}
                    title="Transfer Task"><ArrowRightLeft className="h-4 w-4" /></button>
                  {currentUser?.role === 'super_admin' && (
                    <button onClick={e => { e.stopPropagation(); openDeleteDialog(task); }}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-task-${task.id}`}><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
        </div>
        {/* Pagination footer */}
        {totalTasks > PAGE_SIZE && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50/60" data-testid="tasks-pagination">
            <span className="text-xs text-slate-500">
              Page <span className="font-semibold text-slate-700">{page}</span> of <span className="font-semibold text-slate-700">{Math.max(1, Math.ceil(totalTasks / PAGE_SIZE))}</span>
              {' · '}
              <span className="text-slate-400">{totalTasks.toLocaleString()} total</span>
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setPage(p => Math.max(1, p - 1)); setSelectedIndex(-1); }}
                disabled={page <= 1}
                data-testid="pagination-prev"
                className="h-8 text-xs"
              >
                ← Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setPage(p => p + 1); setSelectedIndex(-1); }}
                disabled={!hasMore}
                data-testid="pagination-next"
                className="h-8 text-xs"
              >
                Next →
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Task Dialog */}
      <CreateTaskDialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)}
        clients={clients} users={users} services={services} canCreate={canCreate}
        currentUser={currentUser} onCreated={fetchData} />

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-task-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTaskInfo?.is_recurring
                ? `"${deleteTaskInfo.title}" is part of a recurring series. Do you want to delete just this occurrence, or the entire recurring series?`
                : 'Are you sure? This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteTaskInfo?.is_recurring && (
            <div className="space-y-2 text-sm px-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="delete-scope" checked={!deleteSeries} onChange={() => setDeleteSeries(false)} data-testid="delete-scope-single" />
                <span>Delete only this occurrence</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="delete-scope" checked={deleteSeries} onChange={() => setDeleteSeries(true)} data-testid="delete-scope-series" />
                <span className="text-red-600 font-medium">Delete the whole recurring series (this + all past & future occurrences)</span>
              </label>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-task-cancel" onClick={() => { setDeleteTaskId(null); setDeleteTaskInfo(null); setDeleteSeries(false); setIsDeleteDialogOpen(false); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTask} className="bg-red-600 hover:bg-red-700" data-testid="delete-task-confirm">
              {deleteSeries ? 'Delete Series' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Shortcut bar */}
      <div className="hidden md:flex fixed bottom-0 left-64 right-0 bg-white/95 backdrop-blur-sm border-t border-slate-200 px-6 py-2.5 items-center justify-between z-40" data-testid="shortcut-bar">
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><Keyboard className="h-3.5 w-3.5" /> Shortcuts:</span>
          {['↑↓ Navigate','T Timer','S Status','C Comment','N New','/ Search','A Select All'].map(s => {
            const [key, ...rest] = s.split(' ');
            return <span key={s} className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono font-semibold text-slate-600">{key}</kbd> {rest.join(' ')}</span>;
          })}
        </div>
        <button onClick={() => setShowShortcuts(true)} className="text-xs text-[#5B6FD6] hover:underline flex items-center gap-1" data-testid="show-all-shortcuts">
          <Command className="h-3 w-3" /> All <kbd className="px-1 py-0.5 bg-slate-100 border rounded text-[10px] font-mono ml-1">?</kbd>
        </button>
      </div>

      <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <DialogContent className="max-w-md" data-testid="shortcuts-help-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Keyboard className="h-5 w-5 text-[#5B6FD6]" /> Keyboard Shortcuts</DialogTitle>
            <DialogDescription>Shortcuts for maximum productivity</DialogDescription></DialogHeader>
          <div className="space-y-1">
            {[['↑ / ↓','Navigate tasks'],['Enter','Open task'],['T','Toggle timer'],['S','Change status'],['C','Quick comment'],['N','New task'],['/','Search'],['Esc','Deselect'],['?','This help'],['A','Select all']].map(([k,d]) => (
              <div key={k} className="flex items-center justify-between py-2 px-1 border-b border-slate-100 last:border-0">
                <span className="text-sm text-slate-700">{d}</span>
                <kbd className="px-2 py-1 bg-slate-100 border rounded text-xs font-mono font-semibold text-slate-600 min-w-[40px] text-center">{k}</kbd>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Task Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="max-w-md" data-testid="transfer-task-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="h-5 w-5 text-[#5B6FD6]" /> Transfer Task</DialogTitle>
            <DialogDescription>Transfer "{transferTask?.title}" to another user. They must accept before the task moves.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-sm font-medium">Transfer To <span className="text-red-500">*</span></Label>
              <Select value={transferToId} onValueChange={setTransferToId}>
                <SelectTrigger className="mt-1.5" data-testid="transfer-to-select">
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.id && u.id !== transferTask?.assignee_id && u.id !== currentUser?.id).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role === 'super_admin' ? 'Super Admin' : u.role === 'admin' ? 'Admin' : 'Member'})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Comment (optional)</Label>
              <Input className="mt-1.5" placeholder="Reason for transfer..." value={transferComment} onChange={e => setTransferComment(e.target.value)} data-testid="transfer-comment-input" />
            </div>
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">The receiver must accept this transfer before the task moves to them.</p>
            <Button className="w-full bg-[#5B6FD6] hover:bg-[#4338CA]" onClick={handleSubmitTransfer} disabled={!transferToId} data-testid="submit-transfer-btn">
              Send Transfer Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ---- Sort helpers ---------------------------------------------------------
// Clickable column header — shows a tiny arrow when active. Sort is applied
// server-side; this just reflects the active column.
function SortHeader({ col, label, sortBy, sortDir, onSort, className = '' }) {
  const active = sortBy === col;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      className={`${className} text-left inline-flex items-center gap-1 hover:text-slate-900 transition-colors ${active ? 'text-slate-900' : ''}`}
      data-testid={`sort-header-${col}`}
      title={`Sort by ${label}${active ? (sortDir === 'asc' ? ' (asc — click to reverse)' : ' (desc — click to reverse)') : ''}`}
    >
      <span>{label}</span>
      <span className={`text-[10px] leading-none ${active ? 'opacity-100 text-[#5B6FD6]' : 'opacity-30'}`}>
        {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  );
}

const RecurringBadge = ({ task, canStop, onStop }) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 ring-1 ring-violet-200 hover:bg-violet-200 transition-colors flex-shrink-0"
          title="Recurring task"
          data-testid={`recurring-badge-${task.id}`}
        >
          <RotateCcw className="h-2.5 w-2.5" />
          <span>{task.recurrence_type || 'recurring'}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start" onClick={(e) => e.stopPropagation()}>
        <p className="text-xs font-semibold text-slate-700 mb-1">Recurring · {task.recurrence_type || 'daily'}</p>
        <p className="text-[11px] text-slate-500 mb-2">
          A new instance is auto-created with the next deadline every time this task is marked completed.
        </p>
        {canStop ? (
          <Button
            size="sm"
            variant="outline"
            className="w-full text-amber-700 border-amber-300 hover:bg-amber-50 gap-1.5"
            onClick={onStop}
            data-testid={`stop-recurrence-btn-${task.id}`}
          >
            <OctagonX className="h-3.5 w-3.5" /> Stop recurrence
          </Button>
        ) : (
          <p className="text-[11px] text-slate-400 italic">Only the assignee, an admin, or super admin can stop this.</p>
        )}
      </PopoverContent>
    </Popover>
  );
};
